import { useEffect, useContext, useState } from 'react';
import { useQueryClient } from 'react-query';
import { apigeeEndpoint } from '../apis';
import { LoadingContext } from '../contexts/LoadingContextProvider';
import * as TFF from '@tff/types/tff';

export type UseInvoluntaryChangesAtcomResponse = {
  involuntaryChangesResponse?: TFF.InvoluntaryTask[];
  setInvoluntaryChangesRequest: (request: InvoluntaryChangesRequest) => void;
  involuntaryChangesRequest?: InvoluntaryChangesRequest;
  errorMessage: string;
  searchDone: boolean | undefined;
};

export type InvoluntaryChangesRequest = {
  product?: string;
  archive?: boolean;
  recordLocator?: string;
};

export const useInvoluntaryChangesAtcom = (): UseInvoluntaryChangesAtcomResponse => {
  const queryClient = useQueryClient();
  const [involuntaryChangesRequest, setInvoluntaryChangesRequest] = useState<InvoluntaryChangesRequest>();
  const [involuntaryChangesResponse, setInvoluntaryChangesResponse] = useState<TFF.InvoluntaryTask[]>([]);
  const { showLoading, closeLoading } = useContext(LoadingContext);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [searchDone, setSearchDone] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const getInvoluntaryChangesAtcom = async (request: InvoluntaryChangesRequest) => {
      const { product, archive, recordLocator } = request;
      showLoading(recordLocator ? `Searching ${recordLocator}` : `Loading involuntary changes for ${product || 'all'}`);
      await queryClient.fetchQuery(
        ['involuntaryChanges', product, archive, recordLocator],
        async () => {
          let url = recordLocator
            ? `/invol-changes-atcom?recordLocator=${recordLocator}`
            : `/invol-changes-atcom?archive=${archive}`;
          if (product && !recordLocator) {
            url += `&product=${product}`;
            // Even if we sent the product with recordLocator the BE logic will still only look for the recordLocator
            // for all products, however this makes it a bit clearer to do it in FE as well
          }
          const response = await apigeeEndpoint.get<TFF.InvoluntaryTask[]>(url);
          setSearchDone(true);
          if ('errorMessage' in response.data) {
            setInvoluntaryChangesResponse([]);
            return [];
          }
          setErrorMessage('');
          setInvoluntaryChangesResponse(response.data);
          return response.data;
        },
        {
          retry: 2,
          staleTime: 1000,
          cacheTime: 50000,
          initialData: () => {
            return queryClient.getQueryData(['involuntaryChanges', product, archive, recordLocator]);
          },
        },
      );
      closeLoading();
    };

    if (involuntaryChangesRequest) {
      void getInvoluntaryChangesAtcom(involuntaryChangesRequest);
    }
  }, [involuntaryChangesRequest]);

  return {
    setInvoluntaryChangesRequest,
    involuntaryChangesRequest,
    involuntaryChangesResponse,
    errorMessage,
    searchDone,
  };
};
